/* eslint-disable indent */

'use client';

import { useEffect, useRef, useState } from 'react';

import { TTextLocalized } from '@northladder/utilities';

interface IFeedbackMessage {
  type: 'error' | 'info' | 'success';
  message: TTextLocalized;
}

interface IUseTouchGestureEvaluationOptions {
  onSuccess?: () => void;
  successMsg: TTextLocalized;
  failureMsg: TTextLocalized;
  /**
   * Whether the test is for two finger or three finger touch gestures.
   */
  testType: 'TWO_FINGERS' | 'THREE_FINGERS';
}

/**
 * This provides common logic for evaluating touch gestures.
 * Currently, it supports evaluating two-finger touch-gestures and three-finger touch-gestures.
 */
export const useTouchGestureEvaluation = ({
  onSuccess,
  successMsg,
  testType,
}: IUseTouchGestureEvaluationOptions) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [feedbackMessage, setFeedbackMessage] =
    useState<IFeedbackMessage | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return () => {};

    const handleOnClickOrTouch = (event: MouseEvent | TouchEvent) => {
      let testPassed = true;

      // Determine the number of touches based on the event type
      if ('touches' in event) {
        const { touches } = event;
        testPassed = touches.length === (testType === 'THREE_FINGERS' ? 3 : 2);
      } else {
        testPassed = false;
      }

      /**
       * Set feedback message based on the test result
       */
      setFeedbackMessage(
        testPassed
          ? { type: 'success', message: successMsg }
          : // making error feedback message as null to avoid intermediate error.
            // Already we are instructing the users to touch with 2 and 3 fingers.
            // { type: 'error', message: failureMsg }
            null
      );

      // Call onSuccess callback if provided and the test passed
      if (testPassed && onSuccess) {
        onSuccess();
      }
    };

    container.addEventListener('touchstart', handleOnClickOrTouch);

    return () => {
      container.removeEventListener('touchstart', handleOnClickOrTouch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  return {
    containerRef,
    feedbackMessage,
  };
};
